import { useEffect } from "react";
import { useLoaderData } from "react-router-dom";
import { QueryClient, useQuery } from "@tanstack/react-query";
import BookingDayCurrent from "@/components/bookings/current/BookingDayCurrent";
import { useAuthStore } from "@/store/AuthStore";
import { useBookingsCurrentStore } from "@/store/BookingsCurrentStore";
import { ApiService } from "@/api/services/ApiService";
import { getTodaysBookings, getWeekDates } from "@/lib/helpers";
import { IBooking } from "@/lib/types";

// ---------------------- API

const bookingsQuery = (bookableResourceId: string) => ({
  queryKey: ["bookings-current", bookableResourceId] as const,
  queryFn: async () => ApiService.bookings.getBookingsCurrent(bookableResourceId!),
});

export const loader = (queryClient: QueryClient) => async () => {
  const bookableResourceId = useAuthStore.getState().userResourceId;

  // userResourceId may still be null when this loader runs in parallel with protectedLoader.
  // Return empty array so the component renders and then fetches once Zustand is populated.
  if (!bookableResourceId) return [] as IBooking[];

  return queryClient.ensureQueryData<IBooking[]>(bookingsQuery(bookableResourceId));
};

// ---------------------- Component

const BookingsListCurrent = () => {
  // --------- Variables / Hooks

  const initialData = useLoaderData() as IBooking[];

  const { userResourceId } = useAuthStore();

  const { setRefetchFunction } = useBookingsCurrentStore();

  const { data: bookings = [], refetch } = useQuery({
    ...bookingsQuery(userResourceId!),
    initialData,
    enabled: !!userResourceId,
  });

  const weekDates = getWeekDates();

  // --------- Effects

  useEffect(() => {
    setRefetchFunction(refetch);
  }, []);

  // --------- JSX

  return (
    <>
      <ul className="flex w-full flex-col items-start justify-start gap-2">
        {weekDates.map((dateInfo) => {
          const todaysBookings = getTodaysBookings(dateInfo.fullDate, bookings ?? []);

          return (
            <li
              key={dateInfo.dayOfWeek}
              className="w-full"
            >
              <BookingDayCurrent
                day={dateInfo.dayOfWeek}
                dayOfMonth={dateInfo.dayOfMonth}
                bookings={todaysBookings}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default BookingsListCurrent;
